// Per spec/components.md §11. Photo (optional) on the left, name + optional
// title + optional signature stacked on the right. When the photo is absent
// the layout collapses to a single-cell stack — avoids the broken-layout
// artifact of an empty left column.
//
// Photo size = `PHOTO_SIZE` for both round and square (D-032); only
// borderRadius differs between the two styles. P-037.22 D-130 bumped
// PHOTO_SIZE 80 → 100 to match Banks's confirmed render size + retina
// upload-target alignment in `src/lib/image-dimensions.ts`.

import { html } from '../render'
import { tokens } from '../tokens'
import { text } from '../primitives/text'
import { image } from '../primitives/image'
import { HtmlString } from '../types'
import { secondaryText } from './helpers'

export type AuthorPhotoStyle = 'round' | 'square'

export interface AuthorBylineProps {
  name: string
  title?: string
  photoUrl?: string
  photoStyle?: AuthorPhotoStyle
  signatureUrl?: string
}

const PHOTO_SIZE = 100
// Cursive signature scans are typically wider than tall; 160px reads cleanly
// inside a 680px container even when stacked next to an 80px photo.
const SIGNATURE_WIDTH = 160

export function authorByline({
  name,
  title,
  photoUrl,
  photoStyle = 'round',
  signatureUrl,
}: AuthorBylineProps): HtmlString {
  const nameHtml = text({ text: name, weight: tokens.type.weight.semibold })
  const titleHtml = title !== undefined ? secondaryText(title) : undefined
  const signatureHtml =
    signatureUrl !== undefined
      ? image({ src: signatureUrl, alt: `${name} signature`, width: SIGNATURE_WIDTH })
      : undefined
  const stack = [nameHtml, titleHtml, signatureHtml]

  if (photoUrl === undefined) {
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td valign="top">${stack}</td></tr></table>`
  }

  const photoHtml = image({
    src: photoUrl,
    alt: name,
    width: PHOTO_SIZE,
    borderRadius: photoStyle === 'round' ? tokens.radius.full : tokens.radius.none,
  })
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td valign="top" width="${PHOTO_SIZE}">${photoHtml}</td><td valign="top" style="padding-left: ${tokens.space[4]}px;">${stack}</td></tr></table>`
}
